import { ChangeDetectionStrategy, Component, Injector, OnInit } from '@angular/core';
import { WidgetBase } from '@fi-app/widget/widget-content/base/widget-base';
import { IForm } from '@fi-shared/fi-form-shared/fi-form-shared';
import { BehaviorSubject } from 'rxjs';
import { ApplicationSettingsType } from '@fi-shared/types/application-settings-type';
import { AccessRightType } from '@fi-shared/types/access-right-type';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { FiFormSharedService } from '@fi-shared/fi-form-shared/fi-form-shared.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { takeUntil } from 'rxjs/operators';
import { FiBackendRequestType, FiRequestsQueueService } from '@fi-shared/fi-shared-services/fi-requests-queue.service';
import { FiAppSettings } from '@fi-shared/fi-app-settings';

@Component({
    selector: 'fi-widget-form-listing',
    template: `
        <div class="FormListingContainer">
          @for (form of formData$ | async; track form) {
            <div class="widget-list-item-item">
              <span class="widget-list-item-icon fa fa-clipboard-list"></span>
              <div class="widget-list-item-content">
                <div class="widget-list-item-header">
                  <a
                    class="widget-list-item-link BrandedColor"
                    [fiStateSref]="FiStateNames.Form.View"
                    [fiStateSrefParams]="{ id: form.Id }"
                    [attr.title]="form.Name"
                    >{{ form.Name }}</a
                    >
                  </div>
                  <div class="clearfix"></div>
                  @if (control.Settings.ShowDescription) {
                    <div class="widget-list-item-summary">
                      {{ form.Description }}
                    </div>
                  }
                  <div class="widget-list-item-date">
                    {{ getFormEndDateText(form) }}
                  </div>
                </div>
              </div>
            }
          </div>
        `,
    styleUrls: ['./form-listing.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class WidgetContentFormListingComponent extends WidgetBase implements OnInit {
    formData$ = new BehaviorSubject<IForm[]>([]);
    FiStateNames = FiStateNames;

    private _isEmpty$ = new BehaviorSubject<boolean>(true);
    get isEmpty$() {
        return this._isEmpty$.asObservable();
    }

    constructor(
        injector: Injector,
        private accessToApplicationTypeService: AccessToApplicationTypeService,
        private formSharedService: FiFormSharedService,
        private fiRequestsQueueService: FiRequestsQueueService,
    ) {
        super(injector);
    }

    ngOnInit() {
        super.ngOnInit();

        const hasAccessToForm = this.accessToApplicationTypeService.HasAccessToApplicationType(
            ApplicationSettingsType.FormLocal,
            AccessRightType.Read,
        );
        if (hasAccessToForm) {
            setTimeout(() => {
                this.loadData();
            }, FiAppSettings.StartDelay);

            this.widgetSharedService
                .getWidgetSettingsChanged$(() => this.control.Settings)
                .pipe(takeUntil(this.ngOnDestroy$))
                .subscribe(() => {
                    this.cdr.markForCheck();
                });
        }
    }

    private loadData() {
        this.fiRequestsQueueService.addRequestToQueue(() => {
            const promise = this.formSharedService.getFormsForWidget().then((data) => {
                this.formData$.next(data);
                this._isEmpty$.next(!data.length);
            });
            return promise;
        }, FiBackendRequestType.WidgetFormListing);
    }

    getFormEndDateText(form: IForm) {
        return this.formSharedService.getFormEndDateTextForWidget(form);
    }
}
